import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import {type PaginatedResult, normalizePagination} from "@/utils/pagination";

export interface CommissionConfig {
  id: string;
  operation_type: string;
  currency: string;
  percentage: string;
  fixed_cents: number;
  min_amount_cents: number;
  max_amount_cents: number;
  promo_label: string;
  promo_active: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CommissionFilters {
  limit?: number;
  offset?: number;
}

export type CommissionListResult = PaginatedResult<CommissionConfig>;

export const getCommissionsService = async (
  filters: CommissionFilters = {}
): Promise<TResponseRequest<CommissionListResult>> => {
  const raw = await httpRequest.get<CommissionConfig[]>({
    url: buildQueryUrl(`${config.walletApiUrl}/bo/commissions`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const createCommissionService = async (body: {
  operation_type: string;
  currency: string;
  percentage: string;
  fixed_cents?: number;
  min_amount_cents?: number;
  max_amount_cents?: number;
  promo_label?: string;
  promo_active?: boolean;
}): Promise<TResponseRequest<CommissionConfig>> => {
  return httpRequest.post<CommissionConfig>({
    url: `${config.walletApiUrl}/bo/commissions`,
    body,
  });
};

export const updateCommissionService = async (
  id: string,
  body: Partial<{
    percentage: string;
    fixed_cents: number;
    min_amount_cents: number;
    max_amount_cents: number;
    promo_label: string;
    promo_active: boolean;
    is_active: boolean;
  }>
): Promise<TResponseRequest<CommissionConfig>> => {
  return httpRequest.put<CommissionConfig>({
    url: `${config.walletApiUrl}/bo/commissions/${encodeURIComponent(id)}`,
    body,
  });
};

export const deleteCommissionService = async (id: string): Promise<TResponseRequest<void>> => {
  return httpRequest.delete<void>({
    url: `${config.walletApiUrl}/bo/commissions/${encodeURIComponent(id)}`,
    body: {},
  });
};
